/**
 * Hook to manage calendar onboarding flow.
 *
 * Storage key is scoped to userId so that signing in as a different user
 * (e.g. after account deletion + fresh sign-up) re-runs onboarding.
 * A one-time migration preserves the completed state for the *first* user
 * who logs in after this scoping change ships, then deletes the legacy
 * global key so subsequent users start fresh.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { useCallback, useEffect, useState } from 'react';

import { useSupabaseAuth } from '@/lib/supabase';

const LEGACY_GLOBAL_KEY = '@fimfam/calendar-onboarding-completed';
const userKey = (userId: string) =>
  `@fimfam/calendar-onboarding-completed:${userId}`;

export function useCalendarOnboarding() {
  const { user } = useSupabaseAuth();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  const checkOnboardingStatus = useCallback(async () => {
    if (!user) {
      setShowOnboarding(false);
      setIsChecking(false);
      return;
    }
    setIsChecking(true);

    try {
      const scopedValue = await AsyncStorage.getItem(userKey(user.id));
      if (scopedValue === 'true') {
        setShowOnboarding(false);
        return;
      }

      // First read after update: an existing user may have completed onboarding
      // under the old global key. Migrate their state to the scoped key and
      // delete the legacy key so the next user (e.g. someone signing up after
      // an account deletion on the same device) sees fresh onboarding.
      const legacyValue = await AsyncStorage.getItem(LEGACY_GLOBAL_KEY);
      if (legacyValue === 'true') {
        await AsyncStorage.multiSet([[userKey(user.id), 'true']]);
        await AsyncStorage.removeItem(LEGACY_GLOBAL_KEY);
        setShowOnboarding(false);
        return;
      }

      setShowOnboarding(true);
    } catch (error) {
      console.error('[useCalendarOnboarding] Error checking status:', error);
      setShowOnboarding(false);
    } finally {
      setIsChecking(false);
    }
  }, [user]);

  useEffect(() => {
    void checkOnboardingStatus();
  }, [checkOnboardingStatus]);

  const handleOnboardingComplete = useCallback(async () => {
    if (!user) {
      setShowOnboarding(false);
      return;
    }
    try {
      await AsyncStorage.setItem(userKey(user.id), 'true');
    } catch (error) {
      console.error('[useCalendarOnboarding] Error saving completion:', error);
    } finally {
      setShowOnboarding(false);
    }
  }, [user]);

  const handleOnboardingSkip = useCallback(async () => {
    if (!user) {
      setShowOnboarding(false);
      return;
    }
    try {
      await AsyncStorage.setItem(userKey(user.id), 'true');
    } catch (error) {
      console.error('[useCalendarOnboarding] Error saving skip:', error);
    } finally {
      setShowOnboarding(false);
    }
  }, [user]);

  const triggerOnboarding = useCallback(() => {
    setShowOnboarding(true);
  }, []);

  return {
    showOnboarding,
    isChecking,
    currentUserName: user?.email?.split('@')[0] || 'User',
    handleOnboardingComplete,
    handleOnboardingSkip,
    triggerOnboarding,
  };
}
